import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import {
  cellCenter,
  KNEE_AXLES,
  KNEE_PILES,
  kneeAxleAt,
  plateFaceNow,
  plateSide,
  platesOut,
  platesPhase,
  SHELL_DRILLS,
  shellAsk,
  shellCell,
  shellChestCuts,
  shellForce,
  shellTurnsRight,
  tornoAt,
  tornoCell,
  tornoSpeed,
  gargOn,
  type BattleState,
} from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A gear. */
const GEAR =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5l1.2 1.6 2-.5.4 2 1.9.9-.7 1.9 1.2 1.6-1.6 1.2.5 2-2 .4-.9 1.9-1.9-.7L8 14.5l-1.2-1.6-2 .5-.4-2-1.9-.9.7-1.9L2 7.4l1.6-1.2-.5-2 2-.4.9-1.9 1.9.7z" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><circle cx="8" cy="8" r="2" fill="currentColor"/></svg>';
/** Tormo's head and shoulders, his eyes. */
const TORMO =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="5" y="1.5" width="6" height="5" rx="1" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M6.6 4h.9M8.5 4h.9" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M1.5 14.5V9.5c0-1.4 1.1-2.5 2.5-2.5h8c1.4 0 2.5 1.1 2.5 2.5v5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 9v3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
/** A hand holding. */
const HAND =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 9V5.5a1 1 0 012 0V8M5 8V4a1 1 0 012 0v4M7 8V3.5a1 1 0 012 0V8M9 8V5a1 1 0 012 0v5c0 2.5-1.8 4.5-4.2 4.5S3 12.6 3 10" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>';
/** A foot coming down, its ring. */
const STOMP =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="12.5" rx="6.5" ry="2" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M6 2.5h4v6.5l1.5 1.5h-7L6 9z" fill="currentColor"/></svg>';
/** The winch's drum and its cable. */
const DRUM =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="7" width="8" height="6" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M4 7v6M6 7v6M8 7v6" stroke="currentColor" stroke-width="0.9" opacity="0.7"/><path d="M10 8.5L14.5 1.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** The wall's crack over the houses. */
const RAJA =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M7 1.5l1.5 3-2 2 2.5 3-1.5 2 1 3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M1.5 14.5h3v-2h2v2M10 14.5v-2h2v2h2.5" fill="none" stroke="currentColor" stroke-width="1.1"/></svg>';
/** The cable frayed. */
const CABLE =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 12c3-1 4-5 6.5-5s3.5 4 6.5 3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M9 6.5l1-2M10.5 8l1.7-1.4M7.5 6.2l-.4-2.2" stroke="currentColor" stroke-width="1" stroke-linecap="round"/></svg>';
/** The mill's sails. */
const MILL = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="1.5" fill="currentColor"/><path d="M8 6.5V1.5h2.5zM9.5 8h5v2.5zM8 9.5v5H5.5zM6.5 8h-5V5.5z" fill="currentColor"/></svg>';
/** A stone with a crack. */
const STONE =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 6l3-3.5h6L14 6l-1 7.5H3z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M8 2.5l-1 4 2 2-1 5" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"/></svg>';
/** The heart's stone. */
const HEART = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 14S2 10 2 5.8A3.2 3.2 0 018 4a3.2 3.2 0 016 1.8C14 10 8 14 8 14z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M8 4.5L7 8l2 1.5L8 12" fill="none" stroke="currentColor" stroke-width="1"/></svg>';
/** Turning round. */
const TWIST = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M13 6.5A5.5 5.5 0 003 5M3 9.5A5.5 5.5 0 0013 11" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M3 1.8V5h3.2M13 14.2V11H9.8" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
/** A plate with its rim. */
const PLATE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 2.5h10l1 5-6 6-6-6z" fill="currentColor" opacity="0.35"/><path d="M3 2.5h10l1 5-6 6-6-6z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>';
/** An eye (where to strike from). */
const SIDE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z" fill="none" stroke="currentColor" stroke-width="1.3"/><circle cx="8" cy="8" r="2" fill="currentColor"/></svg>';
/** A warning. */
const WARN = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5l6.5 12.5h-13z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M8 6v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="8" cy="12" r="0.9" fill="currentColor"/></svg>';

/** Seconds to a tick, at least 1. */
const secs = (s: BattleState, at: number): number => Math.max(1, Math.ceil((at - s.tick) / TICK_HZ));
/** Seconds to a wave time, at least 1. */
const wsecs = (s: BattleState, at: number): number => Math.max(1, Math.ceil((at - s.waveT) / TICK_HZ));

type Mark = { el: HTMLButtonElement; label: HTMLElement; state: HTMLElement; slot: number; point: THREE.Vector3; at?: (s: BattleState) => { x: number; y: number } | null };

/** The stones' names (left shoulder, right shoulder, back). */
const STONE_KEY = ['senda.tormo.stoneL', 'senda.tormo.stoneR', 'senda.tormo.stoneB'];
/** The plates' places (chest, knees, shoulders, back, nape). */
const PLATE_KEY = ['senda.tormo.pChest', 'senda.tormo.pKneeL', 'senda.tormo.pKneeR', 'senda.tormo.pShoulderL', 'senda.tormo.pShoulderR', 'senda.tormo.pBack', 'senda.tormo.pNape'];
/** The joints' names. */
const JOINT_KEY = ['senda.tormo.kneeL', 'senda.tormo.kneeR', 'senda.tormo.elbowL', 'senda.tormo.elbowR'];

/**
 * Tormo on screen (docs/sendas/artifice.md, chapter 5), inside a path's own plate (game/senda-hud.ts) and over the
 * board; a phone's plate kept under about a quarter of the screen:
 *
 * - the knees (`knees`): his four joints as chips (locked or not); how far he is from Nadd; whether the hero carries a
 *   gear; his stomp and the hall's quake. Over the board, with no gear in its hands, a mark on each heap; with one, a
 *   mark on each free axle of the joints still free, following Tormo as he walks: «toca 1 y 4» (the gears it touches,
 *   counted from the one with the arrow), and «gira suelto» on one tried in vain.
 * - the winch (`torno`): the box's pull against Tormo's mark; the spans to the Raja and his next climb (or the haul);
 *   the cable's blows and the gargoyles on it; the gear in the hero's hands. Over the board a mark on each slot (A, B1,
 *   B2, C: its teeth or empty), each gear on the shelf and the lever.
 * - the shell (`shell`, the test): what the drills in ask of the mill (the chest's included); each stone's force and
 *   cut; the chest's drill and the heart's cracks; the next twist and the cable's twists left. Over the board a mark on
 *   each drill (its force; out, cutting which stone, backwards, stopped), a tag on the chest's and a mark on each lever.
 * - Tormo (`plates`): the plates (out, dark, glowing and where); his iron; whether the hero stands on the plate's side;
 *   what is warned (the fist, the stamp, the hammer, the dust). Over the board a tag on the glowing plate.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class TormoHud {
  readonly panel: HTMLElement;
  private kind: 'knees' | 'torno' | 'shell' | 'plates';
  private v = new THREE.Vector3();
  private s: BattleState;
  private rowA = h('div.bh-row');
  private textA = h('span.al-wave');
  private rowB = h('div.bh-row.al-waverow');
  private textB = h('span.al-wave');
  private rowC = h('div.bh-row.al-waverow');
  private textC = h('span.al-wave');
  private rowD = h('div.bh-row.al-waverow');
  private textD = h('span.al-wave');
  private bar = h('i.cb-bar');
  private chips: HTMLElement[] = [];
  private pips: HTMLElement[] = [];
  private pips2: HTMLElement[] = [];
  private marks: Mark[] = [];

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    this.s = s;
    const sr = s.rules?.senda;
    this.kind = sr?.knees ? 'knees' : sr?.torno ? 'torno' : sr?.shell ? 'shell' : 'plates';
    const rows: HTMLElement[] = [];
    if (sr?.knees) {
      const r = sr.knees;
      this.chips = r.joints.map((_, j) => h('b.oj-chip.tm-joint', {}, t(JOINT_KEY[j] as string)));
      this.rowA.append(h('span.bh-ico', { html: GEAR }), h('span.oj-chips', {}, ...this.chips));
      this.rowB.append(h('span.bh-ico', { html: TORMO }), this.textB, h('span.cb-track', {}, this.bar));
      this.rowC.append(h('span.bh-ico', { html: HAND }), this.textC);
      this.rowD.append(h('span.bh-ico', { html: STOMP }), this.textD);
      rows.push(this.rowA, this.rowB, this.rowC, this.rowD);
      this.kneeMarks(s);
    } else if (sr?.torno) {
      const r = sr.torno;
      this.rowA.append(h('span.bh-ico', { html: DRUM }), this.textA);
      this.pips = Array.from({ length: r.spans }, () => h('i'));
      this.rowB.append(h('span.bh-ico', { html: RAJA }), h('span.al-pips.cb-pips.tm-spans', {}, ...this.pips), this.textB, h('span.cb-track', {}, this.bar));
      this.rowC.append(h('span.bh-ico', { html: CABLE }), this.textC);
      this.rowD.append(h('span.bh-ico', { html: HAND }), this.textD);
      rows.push(this.rowA, this.rowB, this.rowC, this.rowD);
      this.tornoMarks(s);
    } else if (sr?.shell) {
      const r = sr.shell;
      this.pips = Array.from({ length: r.mill }, () => h('i'));
      this.rowA.append(h('span.bh-ico', { html: MILL }), h('span.bh-label', {}, t('senda.tormo.mill')), h('span.al-pips.cb-pips.oj-force', {}, ...this.pips), this.textA);
      this.rowB.append(h('span.bh-ico', { html: STONE }), this.textB);
      this.pips2 = Array.from({ length: r.cracks }, () => h('i'));
      this.rowC.append(h('span.bh-ico', { html: HEART }), this.textC, h('span.al-pips.cb-pips.tm-cracks', { title: t('senda.tormo.cracksHint') }, ...this.pips2));
      this.rowD.append(h('span.bh-ico', { html: TWIST }), this.textD);
      rows.push(this.rowA, this.rowB, this.rowC, this.rowD);
      this.shellMarks(s);
    } else if (sr?.plates) {
      const r = sr.plates;
      this.pips = r.order.map(() => h('i'));
      this.rowA.append(h('span.bh-ico', { html: PLATE }), h('span.al-pips.cb-pips.tm-plates', {}, ...this.pips), this.textA);
      this.rowB.append(h('span.bh-ico', { html: TORMO }), this.textB, h('span.cb-track', {}, this.bar));
      this.rowC.append(h('span.bh-ico', { html: SIDE }), this.textC);
      this.rowD.append(h('span.bh-ico', { html: WARN }), this.textD);
      rows.push(this.rowA, this.rowB, this.rowC, this.rowD);
      this.plateMarks();
    }
    this.panel = h(`div.bh.tl-panel.cb-panel.oj-panel.tm-panel.tm-${this.kind}`, {}, ...rows);
  }

  private addMark(slot: number, kind: string, test: string, hint: string, x: number, y: number, at?: Mark['at'], tag = false): Mark {
    const label = h('b.tl-teeth');
    const state = h('small.al-state.tl-state');
    const el = h(
      `button.al-stone.tl-slot.cb-slot.oj-slot.tm-slot.${kind}${tag ? '.tm-tag' : ''}`,
      { type: 'button', 'data-test': test, 'aria-label': t(hint), title: t(hint), onpointerup: (e: Event) => (e.stopPropagation(), tag ? undefined : this.mark(slot)) },
      h('span.al-ring'),
      label,
      state,
    ) as HTMLButtonElement;
    if (tag) el.tabIndex = -1;
    this.board.append(el);
    const m: Mark = { el, label, state, slot, point: simToWorld(x, y, new THREE.Vector3(), 0.3), ...(at ? { at } : {}) };
    this.marks.push(m);
    return m;
  }

  /* ---------- the knees ---------- */

  private kneeMarks(s: BattleState): void {
    const r = s.rules?.senda?.knees;
    if (!r) return;
    r.piles.forEach((p, i) => {
      const m = this.addMark(KNEE_PILES + i, 'pile', `knee-pile-${i}`, 'senda.tormo.pileHint', p.x, p.y);
      m.label.innerHTML = GEAR;
      m.state.textContent = t('senda.tormo.pileMark');
    });
    r.joints.forEach((J, j) =>
      J.free.forEach((f, k) => {
        const m = this.addMark(j * KNEE_AXLES + k, 'axle', `knee-axle-${j}-${k}`, 'senda.tormo.axleHint', 0, 0, (st) => {
          const w = st.senda?.knees;
          return w ? kneeAxleAt(r, w, j, k) : null;
        });
        m.label.textContent = 'abc'[k] ?? '';
        m.state.textContent = t('senda.tormo.touches', { a: f.a + 1, b: f.b + 1 });
      }),
    );
  }

  private updateKnees(s: BattleState): void {
    const r = s.rules?.senda?.knees;
    const w = s.senda?.knees;
    if (!r || !w) return;
    this.chips.forEach((c, j) => c.classList.toggle('done', !!w.locked[j]));
    const steps = Math.max(0, Math.round((w.gap - r.grab) / 1000));
    const b = w.down >= 0 ? t('senda.tormo.fallen') : t('senda.tormo.gap', { n: steps });
    if (this.textB.textContent !== b) this.textB.textContent = b;
    setStyle(this.bar, 'width', `${Math.max(0, Math.min(100, Math.round(((w.gap - r.grab) * 100) / (r.gap - r.grab))))}%`);
    this.rowB.classList.toggle('warn', w.down < 0 && w.gap < r.grab + 3000);
    const c = w.down >= 0 ? '' : w.hand ? t('senda.tormo.holding') : t('senda.tormo.fetch');
    if (this.textC.textContent !== c) this.textC.textContent = c;
    this.rowC.hidden = !c;
    this.rowC.classList.toggle('good', w.hand);
    const d = w.down >= 0 ? '' : w.stompAt >= 0 ? t('senda.tormo.stomp', { s: secs(s, w.stompAt) }) : w.quakeAt >= 0 ? t('senda.tormo.quake', { s: secs(s, w.quakeAt), n: w.locked.filter((x) => !x).length }) : '';
    if (this.textD.textContent !== d) this.textD.textContent = d;
    this.rowD.hidden = !d;
    this.rowD.classList.toggle('warn', !!d);
    for (const m of this.marks) {
      if (m.slot >= KNEE_PILES) {
        m.el.classList.toggle('gone', w.hand || w.down >= 0);
        const sent = w.job === m.slot;
        m.el.classList.toggle('sent', sent);
        m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, r.take)).toFixed(2) : '0');
        m.el.disabled = s.result !== 'running';
        continue;
      }
      const j = Math.floor(m.slot / KNEE_AXLES);
      const k = m.slot % KNEE_AXLES;
      const tried = !!(((w.tried[j] as number) >> k) & 1);
      m.el.classList.toggle('gone', !w.hand || !!w.locked[j] || w.down >= 0);
      m.el.classList.toggle('wrong', tried);
      const f = r.joints[j]?.free[k];
      const st = tried ? t('senda.tormo.spins') : f ? t('senda.tormo.touches', { a: f.a + 1, b: f.b + 1 }) : '';
      if (m.state.textContent !== st) {
        m.state.textContent = st;
        remeasureMark(m.el);
      }
      const sent = w.job === m.slot;
      m.el.classList.toggle('sent', sent);
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, r.stand)).toFixed(2) : '0');
      m.el.disabled = s.result !== 'running';
    }
  }

  /* ---------- the winch ---------- */

  private tornoMarks(s: BattleState): void {
    const r = s.rules?.senda?.torno;
    if (!r) return;
    const R = s.map.radius;
    const names = ['A', 'B1', 'B2', 'C'];
    for (let k = 0; k < 4; k++) {
      const p = cellCenter(R, tornoCell(r, k));
      // (B1 and B2 share an axle, side by side: their marks pulled apart, or their words overlap on a phone.)
      const dx = k === 1 ? -500 : k === 2 ? 500 : 0;
      const dy = k === 1 ? 350 : k === 2 ? -350 : 0;
      const m = this.addMark(k, 'slot', `torno-slot-${k}`, 'senda.tormo.slotHint', p.x + dx, p.y + dy);
      m.label.textContent = names[k] as string;
    }
    r.pieces.forEach((teeth, g) => {
      const p = cellCenter(R, tornoCell(r, 4 + g));
      const m = this.addMark(4 + g, 'shelf', `torno-shelf-${g}`, 'senda.tormo.shelfHint', p.x, p.y + 250);
      m.label.textContent = String(teeth);
    });
    const lv = cellCenter(R, r.lever);
    const m = this.addMark(4 + r.pieces.length, 'lever', 'torno-lever', 'senda.tormo.leverHint', lv.x, lv.y);
    m.label.textContent = t('senda.tormo.pull');
  }

  private updateTorno(s: BattleState): void {
    const r = s.rules?.senda?.torno;
    const w = s.senda?.torno;
    if (!r || !w) return;
    const v = tornoSpeed(r, w);
    const pull = (num: number, den: number): string => (num > 0 ? ((r.input * den) / num).toFixed(1).replace(/\.0$/, '') : '—');
    const want = pull(r.wantNum, r.wantDen);
    const strong = v.den > 0 && v.num * r.wantDen <= r.wantNum * v.den;
    const a = v.den === 0 ? t('senda.tormo.boxLoose', { m: want }) : t('senda.tormo.force', { n: pull(v.num, v.den), m: want });
    if (this.textA.textContent !== a) this.textA.textContent = a;
    this.rowA.classList.toggle('good', strong);
    this.rowA.classList.toggle('warn', v.den > 0 && !strong);
    this.pips.forEach((p, i) => p.classList.toggle('on', i < w.spans));
    const b = w.end >= 0 ? t('senda.tormo.free') : w.haul >= 0 ? t('senda.tormo.hauling', { n: Math.floor((w.hauled * 100) / r.haul) }) : t('senda.tormo.spans', { n: w.spans, s: wsecs(s, w.climb) });
    if (this.textB.textContent !== b) this.textB.textContent = b;
    setStyle(this.bar, 'width', `${Math.min(100, Math.floor((w.hauled * 100) / r.haul))}%`);
    this.rowB.classList.toggle('warn', w.end < 0 && w.spans <= 1);
    this.rowB.classList.toggle('good', w.haul >= 0 || w.end >= 0);
    const perched = gargOn(s, 2);
    const c = w.showerAt >= 0 ? t('senda.tormo.shower', { s: secs(s, w.showerAt) }) : t(perched ? 'senda.tormo.cablePerched' : 'senda.tormo.cable', { n: w.cable, of: r.cable, k: perched });
    if (this.textC.textContent !== c) this.textC.textContent = c;
    this.rowC.classList.toggle('warn', perched > 0 || w.cable * 2 <= r.cable || w.showerAt >= 0);
    const d = w.hand >= 0 ? t('senda.tormo.inHand', { n: r.pieces[w.hand] ?? 0 }) : w.last === 2 && s.tick - w.lastAt < 4 * TICK_HZ ? t('senda.tormo.slipped') : '';
    if (this.textD.textContent !== d) this.textD.textContent = d;
    this.rowD.hidden = !d;
    this.rowD.classList.toggle('warn', w.hand < 0 && !!d);
    for (const m of this.marks) {
      let st = '';
      if (m.slot < 4) {
        const g = tornoAt(w, m.slot);
        st = g >= 0 ? t('senda.tormo.teeth', { n: r.pieces[g] ?? 0 }) : t('senda.tormo.empty');
        m.el.classList.toggle('full', g >= 0);
      } else if (m.slot < 4 + r.pieces.length) {
        const g = m.slot - 4;
        m.el.classList.toggle('gone', w.on[g] !== m.slot);
      } else {
        st = w.haul >= 0 || w.end >= 0 ? t('senda.tormo.pulling') : v.den === 0 ? t('senda.tormo.boxEmpty') : '';
        m.el.classList.toggle('ready', v.den > 0 && w.haul < 0);
      }
      if (m.state.textContent !== st) {
        m.state.textContent = st;
        remeasureMark(m.el);
      }
      const sent = w.job === m.slot;
      m.el.classList.toggle('sent', sent);
      const need = m.slot === 4 + r.pieces.length ? r.pull : r.stand;
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, need)).toFixed(2) : '0');
      if (w.end >= 0) m.el.classList.add('gone');
      m.el.disabled = s.result !== 'running';
    }
  }

  /* ---------- the shell ---------- */

  private shellMarks(s: BattleState): void {
    const r = s.rules?.senda?.shell;
    if (!r) return;
    const R = s.map.radius;
    r.drills.forEach((d, i) => {
      const p = cellCenter(R, shellCell(r, i));
      const m = this.addMark(i, 'drill', `shell-drill-${i}`, 'senda.tormo.drillHint', (p.x + d.at.x) >> 1, (p.y + d.at.y) >> 1);
      m.label.textContent = String(d.force);
    });
    // The chest's drill: a tag (it goes in and out with its gang's).
    const c = this.addMark(-1, 'chest', 'shell-chest', 'senda.tormo.chestHint', r.chestAt.x, r.chestAt.y + 250, undefined, true);
    c.label.textContent = t('senda.tormo.chestLabel');
    r.levers.forEach((cell, b) => {
      const p = cellCenter(R, cell);
      const m = this.addMark(SHELL_DRILLS + b, 'lever', `shell-lever-${b}`, 'senda.tormo.leverHint2', p.x + (b === 0 ? -450 : 450), p.y);
      m.label.textContent = b === 0 ? 'I' : 'II';
    });
  }

  private updateShell(s: BattleState): void {
    const r = s.rules?.senda?.shell;
    const w = s.senda?.shell;
    if (!r || !w) return;
    const ask = shellAsk(r, w);
    this.pips.forEach((p, i) => p.classList.toggle('on', i < ask));
    const a = t('senda.tormo.ask', { n: ask, of: r.mill });
    if (this.textA.textContent !== a) this.textA.textContent = a;
    this.rowA.classList.toggle('warn', ask >= r.mill);
    const stone = (k: number): string => {
      const name = t(STONE_KEY[k] as string);
      if ((w.down[k] as number) >= 0) return t('senda.tormo.stoneDown', { name });
      const f = shellForce(s, r, w, k);
      const need = r.need[k] as number;
      return f >= need ? t('senda.tormo.stoneCut', { name, n: Math.floor(((w.cut[k] as number) * 100) / r.cut) }) : t('senda.tormo.stoneForce', { name, n: f, of: need });
    };
    const b = [0, 1, 2].map(stone).join(' · ');
    if (this.textB.textContent !== b) this.textB.textContent = b;
    this.rowB.classList.toggle('good', [0, 1, 2].some((k) => (w.down[k] as number) < 0 && shellForce(s, r, w, k) >= (r.need[k] as number)));
    const cuts = shellChestCuts(r, w);
    const c = !w.inn[r.gang] ? t('senda.tormo.chestOut', { n: r.gang + 1 }) : cuts ? t('senda.tormo.chestCuts') : t('senda.tormo.chestBack', { n: r.chestForce });
    if (this.textC.textContent !== c) this.textC.textContent = c;
    this.pips2.forEach((p, i) => p.classList.toggle('on', i < w.cracks));
    this.rowC.classList.toggle('warn', cuts || w.cracks > 0);
    const left = Math.max(0, r.twist.holds - w.twists);
    const d = w.end >= 0 ? '' : w.twistAt >= 0 ? t('senda.tormo.twistNow', { s: secs(s, w.twistAt) }) : t('senda.tormo.twist', { s: wsecs(s, w.twist), n: left });
    if (this.textD.textContent !== d) this.textD.textContent = d;
    this.rowD.hidden = !d;
    this.rowD.classList.toggle('warn', w.twistAt >= 0 || left <= 1);
    for (const m of this.marks) {
      let st: string;
      if (m.slot === -1) {
        st = !w.inn[r.gang] ? t('senda.tormo.still') : cuts ? t('senda.tormo.cutsMark') : t('senda.tormo.backMark');
        m.el.classList.toggle('wrong', cuts);
        m.el.classList.toggle('full', !!w.inn[r.gang] && !cuts);
      } else if (m.slot < SHELL_DRILLS) {
        const i = m.slot;
        const stalled = s.tick < (w.stall[i] as number);
        const right = shellTurnsRight(r, w, i);
        const k = (r.reach[w.side] as number[])[i] as number;
        const to = k >= 0 && (w.down[k] as number) < 0 ? t(STONE_KEY[k] as string) : t('senda.tormo.nothing');
        st = !w.inn[i] ? t('senda.tormo.outMark', { to }) : stalled ? t('senda.tormo.stallMark') : right ? t('senda.tormo.cutMark', { to }) : t('senda.tormo.backMark');
        m.el.classList.toggle('full', !!w.inn[i] && right && !stalled);
        m.el.classList.toggle('wrong', !!w.inn[i] && !right);
        m.el.classList.toggle('gang', i === r.gang);
      } else {
        const b2 = m.slot - SHELL_DRILLS;
        st = w.belts[b2] ? t('senda.tormo.turnedMark') : t('senda.tormo.asIsMark');
      }
      if (m.state.textContent !== st) {
        m.state.textContent = st;
        remeasureMark(m.el);
      }
      const sent = w.job === m.slot;
      m.el.classList.toggle('sent', sent);
      const need = m.slot < SHELL_DRILLS ? r.clutch : r.lever;
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, need)).toFixed(2) : '0');
      m.el.classList.toggle('gone', w.end >= 0);
      if (m.slot >= 0) m.el.disabled = s.result !== 'running';
    }
  }

  /* ---------- Tormo ---------- */

  private plateMarks(): void {
    const m = this.addMark(
      -2,
      'plate',
      'tormo-plate',
      'senda.tormo.plateHint',
      0,
      0,
      (st) => {
        const w = st.senda?.plates;
        const e = w ? st.enemies.find((x) => x.id === w.id) : undefined;
        if (!w || !e || w.free >= 0) return null;
        const f = plateFaceNow(e, w);
        const a = (f / 1024) * Math.PI * 2;
        return { x: e.x + Math.round(Math.cos(a) * 1300), y: e.y + Math.round(Math.sin(a) * 1300) };
      },
      true,
    );
    m.label.innerHTML = PLATE;
  }

  private updatePlates(s: BattleState): void {
    const r = s.rules?.senda?.plates;
    const w = s.senda?.plates;
    if (!r || !w) return;
    const out = platesOut(r, w);
    this.pips.forEach((p, i) => {
      p.classList.toggle('on', i < out);
      p.classList.toggle('dark', i < r.dark);
      p.classList.toggle('next', i === out && w.free < 0);
    });
    const ph = platesPhase(r, w);
    const a = w.free >= 0 ? t('senda.tormo.freeTormo') : t('senda.tormo.glows', { name: t(PLATE_KEY[w.slot] as string), n: out, of: r.order.length, p: ph });
    if (this.textA.textContent !== a) this.textA.textContent = a;
    const pct = Math.round((w.iron * 100) / Math.max(1, r.iron));
    const b = t('senda.tormo.iron', { n: pct });
    if (this.textB.textContent !== b) this.textB.textContent = b;
    setStyle(this.bar, 'width', `${pct}%`);
    this.rowB.classList.toggle('warn', pct <= 50);
    const e = s.enemies.find((x) => x.id === w.id);
    const hero = s.heroes[0];
    const on = !!e && !!hero && plateSide(r, w, e, hero.x, hero.y);
    const c = w.free >= 0 ? '' : on ? t('senda.tormo.onSide') : t('senda.tormo.offSide');
    if (this.textC.textContent !== c) this.textC.textContent = c;
    this.rowC.hidden = !c;
    this.rowC.classList.toggle('good', on);
    this.rowC.classList.toggle('warn', !on);
    const d =
      w.free >= 0
        ? ''
        : w.hammer >= 0
          ? t('senda.tormo.hammer', { s: secs(s, w.hammer) })
          : w.dust >= 0
            ? t('senda.tormo.dust', { s: secs(s, w.dust) })
            : w.stampAt >= 0
              ? t('senda.tormo.stamp', { s: secs(s, w.stampAt) })
              : w.fist >= 0
                ? t('senda.tormo.fist', { s: secs(s, w.fist) })
                : s.tick < w.hide
                  ? t('senda.tormo.hides')
                  : '';
    if (this.textD.textContent !== d) this.textD.textContent = d;
    this.rowD.hidden = !d;
    this.rowD.classList.toggle('warn', !!d);
    for (const m of this.marks) {
      const st = on ? t('senda.tormo.hereMark') : t('senda.tormo.sideMark');
      if (m.state.textContent !== st) {
        m.state.textContent = st;
        remeasureMark(m.el);
      }
      m.el.classList.toggle('full', on);
      m.el.classList.toggle('gone', w.free >= 0);
    }
  }

  update(s: BattleState): void {
    this.s = s;
    if (this.kind === 'knees') this.updateKnees(s);
    else if (this.kind === 'torno') this.updateTorno(s);
    else if (this.kind === 'shell') this.updateShell(s);
    else this.updatePlates(s);
  }

  /** Every frame: the marks over their places (the moving ones from the sim's state of the tick). */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      if (m.at) {
        const p = m.at(this.s);
        if (!p) {
          setStyle(m.el, 'display', 'none');
          continue;
        }
        simToWorld(p.x, p.y, m.point, m.slot === -2 ? 2.2 : 0.9);
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
